import type {
  GaborGrangerDesign,
  GaborGrangerStartPriceRange,
  InterviewItem,
  InterviewItemTemplate,
} from "../types";
import seedrandom from "seedrandom";

export * from "./ladder";

export const DEFAULT_ACCEPT_INTENT_THRESHOLD = 4; // top-2-box on the 1-5 intent scale
export const DEFAULT_CURRENCY_CODE = "USD";

// A usable starting ladder the author can adjust. Without prices the interview
// has no rungs to show, so both creating a question and converting an existing
// one to gabor_granger fall back to this.
export const DEFAULT_GABOR_GRANGER_PRICE_POINTS = [5, 10, 15, 20, 25];

// Longest ladder an author may set. Practitioners rarely exceed 9 rungs, so this
// leaves room for real cases while ruling out 20+ ladders that exhaust respondents.
export const MAX_GABOR_GRANGER_PRICE_POINTS = 15;

// Permissive so creation state (optional-and-nullable) satisfies it too,
// mirroring max diff's MaxDiffSizedItem.
export type GaborGrangerStimulusItem = {
  pricePoints?: InterviewItemTemplate["pricePoints"] | null;
  acceptIntentThreshold?: InterviewItemTemplate["acceptIntentThreshold"] | null;
  currencyCode?: InterviewItemTemplate["currencyCode"] | null;
  startPriceRange?: InterviewItemTemplate["startPriceRange"] | null;
};

// Ascending, de-duplicated, finite. The bracket search assumes a higher index
// means a higher price, so normalize rather than trust the author's order.
export const getPriceLadder = (
  item: Pick<GaborGrangerStimulusItem, "pricePoints">
): number[] =>
  Array.from(new Set(item.pricePoints ?? []))
    .filter((price) => Number.isFinite(price))
    .sort((a, b) => a - b);

export const getAcceptIntentThreshold = (
  item: Pick<GaborGrangerStimulusItem, "acceptIntentThreshold">
): number => item.acceptIntentThreshold ?? DEFAULT_ACCEPT_INTENT_THRESHOLD;

export const getCurrencyCode = (
  item: Pick<GaborGrangerStimulusItem, "currencyCode">
): string => item.currencyCode ?? DEFAULT_CURRENCY_CODE;

// Randomizing the start rung mitigates anchoring, which a fixed start would bias
// one way for the whole sample. Seeded off `seedBase` so it survives a resume.
export const getStartPriceRange = (
  item: Pick<GaborGrangerStimulusItem, "startPriceRange">
): GaborGrangerStartPriceRange => item.startPriceRange ?? "all";

// Inclusive indices the random start may draw from. The `middle` window is centred
// exactly, so its size follows the ladder's parity rather than a strict third: an
// even window on an odd ladder would bias every respondent's anchor one way.
export const getAllowedStartIndexRange = (
  item: Pick<GaborGrangerStimulusItem, "pricePoints" | "startPriceRange">
): { lo: number; hi: number } => {
  const n = getPriceLadder(item).length;
  if (n === 0) return { lo: 0, hi: 0 };
  if (getStartPriceRange(item) === "all") return { lo: 0, hi: n - 1 };

  // Parity-matched candidates, nearest to n/3 wins. No tie is possible: a tie
  // needs n/3 to be an integer of opposite parity to n, and n = 3m always gives
  // m = n (mod 2).
  let k = 1;
  let best = Infinity;
  for (let c = n % 2 === 0 ? 2 : 1; c <= n; c += 2) {
    const distance = Math.abs(c - n / 3);
    if (distance < best) {
      best = distance;
      k = c;
    }
  }
  const lo = (n - k) / 2;
  return { lo, hi: lo + k - 1 };
};

export const buildGaborGrangerDesign = (
  item: InterviewItem | InterviewItemTemplate,
  seedBase?: string
): GaborGrangerDesign => {
  const priceCount = getPriceLadder(item).length;
  if (priceCount === 0) return { startIndex: 0 };

  const { lo, hi } = getAllowedStartIndexRange(item);
  const rng = seedrandom(`${seedBase ?? "preview"}:${item.id}`);
  return { startIndex: lo + Math.floor(rng() * (hi - lo + 1)) };
};

// Rungs to budget for the progress bar and time estimate. The real count is per
// respondent, so this is the midpoint of 1..priceCount, not a guarantee.
export const getGaborGrangerEstimatedRungs = (
  item: InterviewItem | InterviewItemTemplate
): number => Math.max(1, Math.ceil(getPriceLadder(item).length / 2));
